/* eslint-disable */

import React from "react";
import moment from "moment";
import {Spinner} from "react-bootstrap";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Label,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import "./timeline.css";
import cropr from "./../shared/utils/cropr";
import gettext from "./../shared/utils/gettext";
import {$, _, interpolate, jsts} from "../shared/utils/legacy";

import TimelineHelper from "./timeline_helper";
import {MEASUREMENTS_META} from "./graph_settings";
import CropFieldTimeline from "./cropfield_timeline";
import {initMoment} from "../shared/utils/i18n";

class Timeline extends React.Component {
    state = {
        ticks: null,
        hasPrecipitationSensor: false,
        recordingData: [],
        satelliteData: [],
        benchmarkData: null,
        precipitationData: [],
        weatherData: [],
        weatherForecast: [],
        graphDomain: [],
        benchmarkFilter: {},
        benchmarkCropfieldCount: null,
        loading: [],
    };

    componentDidMount() {
        this.initTimeline();
        this.loadData();
    }

    initTimeline() {
        initMoment();
        const ticks = TimelineHelper.getFirstDaysOfMonths(
            this.props.cropfield.getDate("period_start"),
            this.props.cropfield.getDate("period_end")
        );
        this.records = TimelineHelper.getRecords(this.props.cropfield);
        this.setState({
            ticks: ticks,
            graphDomain: [ticks[3], ticks[ticks.length - 1]],
        });
    }

    shouldShowBenchMark() {
        return (
            cropr.features.cloudfarm_contract &&
            _.contains(cropr.features.cloudfarm_contract_cropfields, this.props.cropfield.id)
        );
    }

    async loadData() {
        this.loadSatellite();
        await this.loadPrecipitationData();
        if (
            cropr.features.dacom_online_weather ||
            (cropr.features.weather_contract &&
                _.contains(cropr.features.weather_contract_cropfields, this.props.cropfield.id))
        ) {
            this.loadWeatherData();
        }
        this.setState({recordingData: TimelineHelper.loadRecordData(this.records)});
        if (this.shouldShowBenchMark()) this.loadBenchmark();
    }

    async loadPrecipitationData() {
        const that = this;
        const now = moment().unix();
        const {cropfield} = this.props;
        const precipitationSensorType = cropr.data.fieldattributetype_list.find(
            ({edi_code}) => edi_code === "linked_precipitation_sensor"
        );
        const cropfieldSensorId =
            Boolean(precipitationSensorType) &&
            (cropfield.get("field_attributes") || [])[precipitationSensorType.id];
        if (cropfieldSensorId) {
            await this.setState({hasPrecipitationSensor: true});
            const start_date = moment.unix(this.state.graphDomain[0]);
            const end_date = moment.unix(this.state.graphDomain[1]);
            const cacheKeyData = `precipitation_${cropfieldSensorId}`;
            const url = `/installations/${cropfieldSensorId}/interpreted_measurements/`;
            const loadingKey = "precipitation";
            this.setState({loading: [...this.state.loading, loadingKey]});
            cropr.d2c.apiClient.get({
                url: url,
                format: "json",
                start: start_date,
                end: end_date,
                success: ({meta, measurements}) => {
                    let data = [];
                    measurements.forEach(function (weatherOnTimestamp) {
                        let measurement = {
                            date: isNaN(weatherOnTimestamp.timestamp)
                                ? moment(weatherOnTimestamp.timestamp + "").unix()
                                : weatherOnTimestamp.timestamp,
                        };
                        let values = weatherOnTimestamp.values;
                        let isForecast = measurement["date"] > now;
                        meta.meta?.map(({key}) => key)
                            .forEach(function (metaItem, i) {
                                measurement[metaItem] = values[i];
                            });

                        if (!isForecast) {
                            data.push({
                                ...measurement,
                                precipitation_sum: measurement.precipitation || null,
                                air_temperature_mean: measurement.air_temperature || null,
                            });
                        }
                    });
                    cropr[cacheKeyData] = data;
                    that.setState({precipitationData: cropr[cacheKeyData]});
                    that.setState({loading: that.state.loading.filter((item) => item !== loadingKey)});
                },
                error: (response) => {
                    that.setState({loading: that.state.loading.filter((item) => item !== loadingKey)});
                },
            });
        }
    }

    loadSatellite() {
        const cf = this.props.cropfield;
        const loadingKey = "satellite";
        const key = `satelliteData_${cf.id}_${cf.get("period_start")}_${cf.get("period_end")}`;
        const wkt = cf.get("geometry");
        if (!wkt) return;

        this.setState({loading: [...this.state.loading, loadingKey]});
        if (cropr[key]) {
            const satelliteData = cropr[key];
            this.setState({satelliteData: satelliteData});
            if (satelliteData.length > 0) {
                this.fetchImage(
                    key,
                    moment.unix(satelliteData[satelliteData.length - 1].date).format("YYYYMMDD"),
                    "ndvi",
                    wkt
                );
            }
            this.setState({loading: this.state.loading.filter((item) => item !== loadingKey)});
            return;
        }

        const url = `${window.origin}/api/v3/satellite/statistics/`;
        fetch(url, {
            method: "POST",
            body: JSON.stringify({
                start_date: cf.get("period_start").split("-").join(""),
                end_date: cf.get("period_end").split("-").join(""),
                wkt: wkt,
                all: "true",
                types: "NDVI,WDVI",
            }),
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": cropr.getCookie("csrftoken"),
            },
        }).then((response) => {
            if (response.ok) {
                response.json().then((result) => {
                    const content = result.content;
                    const data = Object.entries(content.dates || {})
                        .filter(([date, stats]) => moment(date).isBefore(cf.getDate("period_end")))
                        .map(([date, stats]) => {
                            return {
                                date: moment(date + "").unix(),
                                ndvi_avg: stats.statistics.NDVI.average,
                            };
                        });
                    this.setState({satelliteData: data});
                    cropr[key] = data;
                    if (result.dates && Object.keys(content.dates).length > 0) {
                        const dates = Object.keys(content.dates);
                        const imageDate = dates[dates.length - 1];
                        this.fetchImage(key, imageDate, "ndvi", wkt);
                    }
                });
            } else {
                cropr.notify("error", gettext("Can't get satellite data for this crop field."));
                this.setState({satelliteData: []});
                cropr[key] = [];
            }
            this.setState({loading: this.state.loading.filter((item) => item !== loadingKey)});
        });
    }

    loadBenchmark() {
        const cf = this.props.cropfield;
        const bf = this.state.benchmarkFilter;
        const key = `benchmark_${cf.id}_${bf.variety || "none"}_${bf.soil_type || "none"}_${
            bf.region || "none"
        }`;
        const state = this.state;
        const loadingKey = "benchmark";
        this.setState({loading: [...this.state.loading, loadingKey]});
        if (cropr[key]) {
            const benchmarkData = cropr[key];
            this.setState({benchmarkData: benchmarkData});
            this.setState({loading: this.state.loading.filter((item) => item !== loadingKey)});
            return;
        }
        let form = new FormData();
        const url = `/apps/cloudfarm/benchmark/${cf.id}/`;
        Object.keys(state.benchmarkFilter).forEach((key) =>
            form.append(key, state.benchmarkFilter[key])
        );
        fetch(url, {
            method: "POST",
            credentials: "same-origin",
            body: form,
            headers: {
                "X-CSRFToken": cropr.getCookie("csrftoken"),
            },
        }).then((response) => {
            if (response.ok) {
                response.json().then((result) => {
                    if (result && result.benchmark_data) {
                        const data = result.benchmark_data.map(([date, ndvi, std]) => {
                            return {
                                date: moment(moment(date).format("YYYY-MM-DD"), "YYYY-MM-DD").unix(), //TODO: cast to date from 00:00h,
                                date_str: moment(moment(date).format("YYYY-MM-DD"), "YYYY-MM-DD").format("LL"),
                                benchmark_ndvi: ndvi,
                                bm_std_min: ndvi - std,
                                bm_std_max: ndvi + std,
                                benchmark_std: std,
                            };
                        });
                        this.setState({benchmarkData: data});
                        this.setState({benchmarkCropfieldCount: result.cropfield_count});
                        cropr[key] = data;
                    }
                });
            } else {
                cropr.notify("error", gettext("Can't get benchmark data for this crop field."));
                this.setState({benchmarkData: []});
                cropr[key] = [];
            }
            this.setState({loading: this.state.loading.filter((item) => item !== loadingKey)});
        });
    }

    loadWeatherData() {
        const that = this;
        const cropField = this.props.cropfield;
        const today = moment();
        const start_date = this.props.cropfield.getDate("period_start").format("YYYY-MM-DD");
        const end_date = this.props.cropfield.getDate("period_end").format("YYYY-MM-DD");
        const cacheKeyData = `weatherData_${cropField.id}}`;
        const cacheKeyForecast = `weatherForecast_${cropField.id}}`;
        const loadingKey = "weather";
        this.setState({loading: [...this.state.loading, loadingKey]});
        if (cropr[cacheKeyData]) {
            this.setState({weatherData: cropr[cacheKeyData]});
            this.setState({weatherForecast: cropr[cacheKeyForecast]});
            this.setState({loading: this.state.loading.filter((item) => item !== loadingKey)});
            return;
        }
        const centroid = cropField.getCentroid(4326);
        if (centroid) {
            const {y: lat, x: lon} = centroid || {};
            cropr.d2c.apiClient.get({
                url: `/daily_weather/`,
                format: "json",
                lat: lat,
                lon: lon,
                start_date,
                end_date,
                meta: "AT,PT",
                success: (data) => {
                    if (!(data && data.daily_weather)) return;
                    let rechartsData = data.daily_weather.map((measurement) => ({
                        ...measurement.values,
                        date: moment(measurement.day, "YYYY-MM-DD").unix(),
                    }));
                    cropr[cacheKeyData] = rechartsData.filter((m) =>
                        moment.unix(m.date).isSameOrBefore(today)
                    );
                    cropr[cacheKeyForecast] = rechartsData
                        .filter((m) => moment.unix(m.date).isSameOrAfter(today))
                        .map((m) => ({
                            date: m.date,
                            precipitation_forecast: m.precipitation_sum,
                            precipitation_deficit_accum_forecast: m.precipitation_deficit_accum,
                            air_temperature_forecast: m.air_temperature_mean,
                        }));
                    that.setState({weatherData: cropr[cacheKeyData] || []});
                    that.setState({weatherForecast: cropr[cacheKeyForecast] || []});
                    that.setState({loading: that.state.loading.filter((item) => item !== loadingKey)});
                },
                error: (response) => {
                    that.setState({loading: that.state.loading.filter((item) => item !== loadingKey)});
                },
            });
        } else {
            this.setState({loading: this.state.loading.filter((item) => item !== loadingKey)});
        }
    }

    fetchImage(key, date, type, wkt) {
        const imageKey = `${key}_${date}_image`;
        if (cropr[imageKey]) {
            this.showImage(cropr[imageKey]);
            return;
        }
        const url = `${window.origin}/api/v3/satellite/images/`;
        fetch(url, {
            method: "POST",
            body: JSON.stringify({
                wkt: wkt,
                date: date,
                adjust: "false",
                legend: type,
            }),
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": cropr.getCookie("csrftoken"),
            },
        }).then((response) => {
            if (response.ok) {
                response.json().then((result) => {
                    const content = result.content;
                    cropr[imageKey] = content;
                    this.showImage(content);
                });
            } else {
                cropr.notify("error", gettext("Can't get latest satellite image for this crop field."));
                cropr[imageKey] = {};
            }
        });
    }

    showImage(imageData) {
        if (
            imageData &&
            imageData.images &&
            Object.keys(imageData.images).length > 0 &&
            cropr.dashboardView &&
            cropr.dashboardView.cropfield
        ) {
            const url = `${window.origin}/api/v3/satellite/image/${
                Object.values(imageData.images)[0]["ndvi.png"]
            }`;
            cropr.dashboardView.cropfield.addImageOverlay(url, imageData.bounding_box);
        }
    }

    simplify(cf) {
        if (!cf.get("geometry")) return null;
        const original = new jsts.io.WKTReader().read(cf.get("geometry"));
        const simplified = jsts.util.simplify(original, 0.00001);
        return new jsts.io.WKTWriter().write(simplified);
    }

    getGraphData() {
        const weatherDataAccum = this.state.weatherData
            .map((m) => ({
                ...m,
                ...(this.state.precipitationData.length
                    ? {
                        precipitation_sum: null,
                    }
                    : {}),
            }))
            .concat(this.state.weatherForecast)
            .concat(this.state.precipitationData);

        let graph_data = [];
        let currentDate = moment(this.props.cropfield.get("period_start"));
        const stopDate = moment(this.props.cropfield.get("period_end"));
        while (currentDate <= stopDate) {
            let currentUnixDate = moment(currentDate).unix();
            let data = {date: currentUnixDate};
            _.each(
                [
                    this.state.recordingData,
                    this.state.satelliteData,
                    this.state.benchmarkData,
                    weatherDataAccum,
                ],
                (list) => {
                    let d = _.find(list || [], (d) => {
                        return d.date === currentUnixDate;
                    });
                    if (d) {
                        data = _.extend(data, d);
                    }
                }
            );
            //replace forecast data
            const forecast_mapping = {
                air_temperature_forecast: "air_temperature_mean",
                precipitation_deficit_accum_forecast: "precipitation_deficit_accum",
            };
            _.each(data, (v, k) => {
                if (k.includes("_forecast") && forecast_mapping[k]) {
                    data[forecast_mapping[k]] = v;
                }
            });
            graph_data.push(data);
            currentDate = moment(currentDate).add(1, "days");
        }
        return graph_data;
    }

    render() {
        return (
            <CropFieldTimeline
                base_url={this.props.base_url}
                cropfield={this.props.cropfield}
                footer={this.props.footer}
                timeline_state={this.state}
                parent={this}
            />
        );
    }
}

class Loader extends React.Component {
    render() {
        return (
            <div style={{width: "100%", height: "150px", textAlign: "{this.props.align}"}}>
                <Spinner animation="border" role="status">
                    <span className="sr-only">{gettext("Loading")}...</span>
                </Spinner>
            </div>
        );
    }
}

export class TimelineDetail extends Timeline {
    DefaultXAsis() {
        const ticks = this.state.ticks;
        const records = cropr.records.filter(
            (record) => record.get("cropfield") === this.props.cropfield.id
        );
        return (
            <XAxis
                dataKey="date"
                domain={this.state.graphDomain}
                name="Date"
                tickFormatter={(date) => moment.unix(date).format("LL")}
                scale="time"
                ticks={ticks}
                interval={0}
                padding={{left: 30, right: 30}}
            />
        );
    }

    changeBenchmarkFilter(e, benchmark_filter) {
        const $target = $(e.target);
        const filter_type = $target.data("filter");
        const checked = $target.is(":checked");
        benchmark_filter[filter_type] = !!checked;
        this.parent.setState({benchmarkFilter: benchmark_filter});
        this.parent.loadBenchmark();
    }

    render() {
        const records = cropr.records.filter(
            (record) =>
                record.get("processing_state") === "performed" &&
                record.get("cropfield") === this.props.cropfield.id
        );
        const plantRecordDate = TimelineHelper.getPlantDate(records);
        const harvestRecordDate = TimelineHelper.getHarvestDate(records);
        const showBenchmarkData = this.state.benchmarkData && this.state.benchmarkData.length > 0;

        const weatherDataAccum = this.state.weatherData
            .map((m) => ({
                ...m,
                ...(this.state.precipitationData.length
                    ? {
                        precipitation_sum: null,
                    }
                    : {}),
            }))
            .concat(this.state.weatherForecast)
            .concat(this.state.precipitationData);
        const showPrecipitationDeficit = Boolean(
            weatherDataAccum.find(
                ({precipitation_deficit_accum, precipitation_deficit_accum_forecast}) =>
                    precipitation_deficit_accum !== null || precipitation_deficit_accum_forecast !== null
            )
        );
        const showPrecipitation = Boolean(
            weatherDataAccum.find(
                ({precipitation_sum, precipitation_forecast}) =>
                    precipitation_sum !== null || precipitation_forecast !== null
            )
        );
        const showTemperature = Boolean(
            weatherDataAccum.find(
                ({air_temperature_forecast, air_temperature_mean}) =>
                    air_temperature_forecast !== null || air_temperature_mean !== null
            )
        );
        let base_url = interpolate("#cropfield/%s/%s/", [
            this.props.cropfield.get("crop") || 0,
            this.props.cropfield.id,
        ]);
        const graph_data = this.getGraphData();

        return (
            <div className="py-4">
                <CropFieldTimeline
                    cropfield={this.props.cropfield}
                    base_url={base_url}
                    benchmark_header={this.shouldShowBenchMark()}
                    timeline_state={this.state}
                    parent={this}
                    changeBenchmarkFilter={this.changeBenchmarkFilter}
                />
                <div className="card mt-3">
                    <div className="card-body">
                        <div className="card-content">
                            <h4>{gettext("Records")}</h4>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart
                                    data={graph_data}
                                    margin={{top: 10, right: 30, left: 0, bottom: 0}}
                                    syncId="cropfield_detail_graphs"
                                >
                                    {this.DefaultXAsis()}
                                    <CartesianGrid strokeDasharray="3 3"/>
                                    <YAxis
                                        label={MEASUREMENTS_META.ndvi.unit}
                                        domain={MEASUREMENTS_META.ndvi.domain}
                                        type="number"
                                    />
                                    <Tooltip
                                        cursor={{strokeDasharray: "3 3"}}
                                        content={TimelineHelper.renderTooltip(records, graph_data, "records")}
                                    />
                                    <ReferenceLine x={moment().unix()} stroke="#A9B6BA">
                                        <Label value={gettext("now")} position="insideTopRight"/>
                                    </ReferenceLine>
                                    {plantRecordDate && (
                                        <ReferenceLine x={moment(plantRecordDate, "DD-MM-YYYY").unix()}
                                                       stroke="#A9B6BA">
                                            <Label value={gettext("Plant")} position="insideTopRight"/>
                                        </ReferenceLine>
                                    )}
                                    {harvestRecordDate && (
                                        <ReferenceLine x={moment(harvestRecordDate, "DD-MM-YYYY").unix()}
                                                       stroke="#A9B6BA">
                                            <Label value={gettext("Harvest")} position="insideTopRight"/>
                                        </ReferenceLine>
                                    )}
                                    <Line
                                        isAnimationActive={false}
                                        name={gettext("Activities")}
                                        dataKey="records"
                                        legendType="circle"
                                        strokeWidth={0}
                                        stroke="#F9C342"
                                        dot={{stroke: "#F9C342", strokeWidth: 5}}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                <div className="card mt-3">
                    <div className="card-body">
                        <div className="card-content">
                            <h4>{gettext("NDVI")}</h4>
                            {this.state.satelliteData.length ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <ComposedChart syncId="cropfield_detail_graphs" data={graph_data}>
                                        {this.DefaultXAsis()}
                                        <CartesianGrid strokeDasharray="3 3"/>
                                        <YAxis
                                            label={MEASUREMENTS_META.ndvi.unit}
                                            domain={MEASUREMENTS_META.ndvi.domain}
                                            type="number"
                                        />
                                        <Tooltip
                                            cursor={{strokeDasharray: "3 3"}}
                                            content={TimelineHelper.renderTooltip(this.records, graph_data, "ndvi_avg")}
                                        />
                                        {showBenchmarkData ? (
                                            <Area
                                                type="monotone"
                                                dataKey="bm_std_max"
                                                stroke={MEASUREMENTS_META.benchmark.stroke}
                                                fill={MEASUREMENTS_META.benchmark.fill}
                                                name={gettext("Benchmark NDVI")}
                                                connectNulls
                                                legendType="none"
                                            />
                                        ) : null}
                                        {showBenchmarkData ? (
                                            <Area
                                                type="monotone"
                                                dataKey="bm_std_min"
                                                stroke={MEASUREMENTS_META.benchmark.stroke}
                                                fill="#F6F7F8"
                                                fillOpacity={1}
                                                name={gettext("Benchmark NDVI")}
                                                connectNulls
                                                legendType="none"
                                            />
                                        ) : null}
                                        <Line
                                            isAnimationActive={false}
                                            type="monotone"
                                            name="NDVI"
                                            dataKey="ndvi_avg"
                                            stroke={MEASUREMENTS_META.ndvi.stroke}
                                            strokeWidth={3}
                                            connectNulls
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            ) : (
                                <Loader align={"left"}/>
                            )}</div>
                    </div>
                </div>
                {(cropr.features.dacom_online_weather ||
                    (cropr.features.weather_contract &&
                        _.contains(cropr.features.weather_contract_cropfields, this.props.cropfield.id))) && (
                    <>
                        <div className="card mt-3">
                            <div className="card-body">
                                <div className="card-content">
                                    <h4>{gettext("Temperature")}</h4>
                                    {showTemperature ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <AreaChart
                                                data={graph_data}
                                                syncId="cropfield_detail_graphs"
                                                margin={{top: 10, right: 30, left: 0, bottom: 0}}
                                            >
                                                <CartesianGrid strokeDasharray="3 3"/>
                                                {this.DefaultXAsis()}
                                                <Tooltip
                                                    cursor={{strokeDasharray: "3 3"}}
                                                    content={TimelineHelper.renderTooltip(
                                                        this.records,
                                                        graph_data,
                                                        "air_temperature_mean"
                                                    )}
                                                />
                                                <YAxis
                                                    label={MEASUREMENTS_META.air_temperature.unit}
                                                    domain={MEASUREMENTS_META.air_temperature.domain}
                                                    allowDataOverflow={MEASUREMENTS_META.air_temperature.clip}
                                                    type="number"
                                                    yAxisId={1}
                                                />
                                                <Area
                                                    isAnimationActive={false}
                                                    dot={false}
                                                    type="monotone"
                                                    name={gettext("Temperature")}
                                                    dataKey="air_temperature_mean"
                                                    stroke="none"
                                                    fill={MEASUREMENTS_META.air_temperature.stroke}
                                                    fillOpacity={0.4}
                                                    // strokeWidth={2}
                                                    connectNulls
                                                    legendType="rect"
                                                    yAxisId={1}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Loader align={"left"}/>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="card mt-3">
                            <div className="card-body">
                                <div className="card-content">

                                    <h4>{gettext("Precipitation")}</h4>
                                    {showPrecipitation ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart
                                                data={graph_data}
                                                syncId="cropfield_detail_graphs"
                                                margin={{top: 10, right: 30, left: 0, bottom: 0}}
                                            >
                                                <CartesianGrid strokeDasharray="3 3"/>
                                                {this.DefaultXAsis()}
                                                <Tooltip
                                                    cursor={{strokeDasharray: "3 3"}}
                                                    content={TimelineHelper.renderTooltip(
                                                        this.records,
                                                        graph_data,
                                                        "precipitation_sum"
                                                    )}
                                                />
                                                <YAxis
                                                    label={MEASUREMENTS_META.precipitation.unit}
                                                    domain={MEASUREMENTS_META.precipitation.domain}
                                                    type="number"
                                                    yAxisId={2}
                                                />
                                                <Bar
                                                    isAnimationActive={false}
                                                    dot={false}
                                                    barSize={2}
                                                    name={gettext("Precipitation")}
                                                    dataKey="precipitation_sum"
                                                    stroke={MEASUREMENTS_META.precipitation.stroke}
                                                    fill={MEASUREMENTS_META.precipitation.stroke}
                                                    legendType="rect"
                                                    connectNulls
                                                    yAxisId={2}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Loader align={"left"}/>
                                    )}</div>
                            </div>
                        </div>
                        <div className="card mt-3">
                            <div className="card-body">
                                <div className="card-content">

                                    <h4>{gettext("Precipitation deficit")}</h4>
                                    {showPrecipitationDeficit ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart
                                                data={graph_data}
                                                syncId="cropfield_detail_graphs"
                                                margin={{top: 10, right: 30, left: 0, bottom: 0}}
                                            >
                                                <CartesianGrid strokeDasharray="3 3"/>
                                                {this.DefaultXAsis()}
                                                <Tooltip
                                                    cursor={{strokeDasharray: "3 3"}}
                                                    content={TimelineHelper.renderTooltip(
                                                        this.records,
                                                        graph_data,
                                                        "precipitation_deficit_accum"
                                                    )}
                                                />
                                                <YAxis
                                                    label={MEASUREMENTS_META.precipitation_deficit.unit}
                                                    domain={MEASUREMENTS_META.precipitation_deficit.domain}
                                                    allowDataOverflow={MEASUREMENTS_META.air_temperature.clip}
                                                    type="number"
                                                    yAxisId={3}
                                                />
                                                <Line
                                                    isAnimationActive={false}
                                                    dot={false}
                                                    type="monotone"
                                                    name={gettext("Precipitation deficit")}
                                                    dataKey="precipitation_deficit_accum"
                                                    stroke={MEASUREMENTS_META.precipitation_deficit.stroke}
                                                    strokeWidth={2}
                                                    legendType="rect"
                                                    connectNulls
                                                    yAxisId={3}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Loader align={"left"}/>
                                    )}</div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }
}

export default Timeline;

// cropr.renderTimeline();
